import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { money } from "@/lib/money";

function uid() {
  return typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `x_${Math.random().toString(36).slice(2, 10)}`;
}

function newCfField(type) {
  const labels = { paid_name: "Name", paid_dropdown: "Option", paid_image: "Upload image", quantity: "Quantity" };
  const field = { id: `cf_${uid().replace(/-/g, "").slice(0, 10)}`, type, label: labels[type] || "Field", required: true, price: 0, help: "", options: [] };
  if (type === "paid_dropdown") field.options = [{ id: `op_${uid().slice(0, 8)}`, label: "Option 1", price: 0 }];
  if (type === "quantity") field.options = [{ id: `op_${uid().slice(0, 8)}`, label: "50 pcs", qty: 50, price: 0 }];
  return field;
}

function blankForm() {
  return {
    name: "Untitled form",
    enabled: true,
    cta: "Get Started",
    fields: [],
    scope: "products",
    category_ids: [],
    product_ids: [],
  };
}

function takenMaps(forms, ignoreId) {
  const products = {};
  const categories = {};
  let allId = null;
  (forms || []).forEach((f) => {
    if (f.id === ignoreId) return;
    if (f.scope === "all") allId = f.id;
    (f.product_ids || []).forEach((id) => { products[id] = f; });
    (f.category_ids || []).forEach((id) => { categories[id] = f; });
  });
  return { products, categories, allId };
}

export default function AdminCustomForm() {
  const [tab, setTab] = useState("forms");
  const [data, setData] = useState(null);
  const [subs, setSubs] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadForms = async () => {
    const { data: d } = await api.get("/merchant/custom-forms");
    setData(d);
    return d;
  };

  useEffect(() => {
    Promise.all([
      loadForms(),
      api.get("/merchant/custom-form/submissions").then(({ data: d }) => setSubs(d)),
      api.get("/merchant/products").then(({ data: d }) => setProducts(Array.isArray(d) ? d : [])).catch(() => {}),
      api.get("/merchant/categories").then(({ data: d }) => setCategories(Array.isArray(d) ? d : [])).catch(() => {}),
    ]).catch((e) => toast.error(extractErr(e)));
  }, []);

  const taken = useMemo(() => takenMaps(data?.items, editing?.id), [data, editing]);

  const save = async () => {
    if (!editing) return;
    if (!editing.name?.trim()) { toast.error("Give the form a name"); return; }
    if (!(editing.fields || []).length) { toast.error("Add at least one field"); return; }
    if (editing.scope === "all" && taken.allId && taken.allId !== editing.id) {
      toast.error("Another form is already linked to all products");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: editing.name,
        enabled: editing.enabled,
        cta: editing.cta,
        fields: editing.fields,
        scope: editing.scope,
        category_ids: editing.scope === "categories" ? editing.category_ids : [],
        product_ids: editing.scope === "products" ? editing.product_ids : [],
      };
      if (editing.id) await api.put(`/merchant/custom-forms/${editing.id}`, payload);
      else await api.post("/merchant/custom-forms", payload);
      toast.success("Form saved");
      const d = await loadForms();
      if (editing.id) setEditing((d.items || []).find((x) => x.id === editing.id) || null);
      else setEditing(null);
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this form?")) return;
    try {
      await api.delete(`/merchant/custom-forms/${id}`);
      if (editing?.id === id) setEditing(null);
      await loadForms();
      toast.success("Form deleted");
    } catch (e) { toast.error(extractErr(e)); }
  };

  const toggleId = (key, id, takenBy) => {
    if (takenBy) return;
    const cur = editing[key] || [];
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    setEditing({ ...editing, [key]: next });
  };

  if (!data) return <div className="text-slate-500">Loading…</div>;
  if (!data.active) {
    return (
      <div className="max-w-2xl space-y-4">
        <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> App Store</Link>
        <Card className="p-8 text-center text-slate-600">Unlock <strong>Custom Form</strong> from the App Store to collect paid product options.</Card>
      </div>
    );
  }

  const items = data.items || [];
  const submissions = subs?.items || [];

  return (
    <div className="max-w-5xl space-y-5">
      <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1" data-testid="cf-back"><ArrowLeft className="w-4 h-4" /> App Store</Link>
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Custom Form</h1>
        <p className="text-sm text-slate-500 mt-1">Create forms here and link them to all products, a category, or chosen products. A product or category can only belong to one form.</p>
      </div>
      <div className="flex gap-1 border-b border-slate-200">
        <button type="button" data-testid="cf-tab-forms" onClick={() => setTab("forms")} className={`px-4 py-2 text-sm font-medium ${tab === "forms" ? "text-slate-900 border-b-2 border-slate-900" : "text-slate-500"}`}>Forms</button>
        <button type="button" data-testid="cf-tab-submissions" onClick={() => setTab("submissions")} className={`px-4 py-2 text-sm font-medium ${tab === "submissions" ? "text-slate-900 border-b-2 border-slate-900" : "text-slate-500"}`}>Submissions</button>
      </div>

      {tab === "submissions" && (
        submissions.length === 0 ? (
          <Card className="p-8 text-center text-slate-500" data-testid="cf-empty">No submissions yet. Link a form to products and wait for an order.</Card>
        ) : (
          <div className="space-y-3">
            {submissions.map((row) => (
              <Card key={row.id} className="p-5 border-slate-200/70 space-y-2" data-testid="cf-submission">
                <div className="flex flex-wrap justify-between gap-2">
                  <div>
                    <div className="font-semibold text-slate-900">{row.product_name}</div>
                    <div className="text-xs text-slate-500">Order {row.order_number} · {row.customer_name} · {row.customer_email}</div>
                  </div>
                  <div className="font-mono-nums font-semibold">{money(row.line_total, row.currency === "USD" ? "$" : "₹")}</div>
                </div>
                <ul className="text-sm text-slate-700 space-y-1">
                  {(row.fields || []).map((f, i) => (
                    <li key={i} className="flex gap-2 flex-wrap">
                      <span className="text-slate-500">{f.label}:</span>
                      {String(f.value || "").startsWith("http") || String(f.value || "").startsWith("/api/") ? (
                        <a href={mediaUrl(f.value)} className="text-blue-600 underline" target="_blank" rel="noreferrer">View file</a>
                      ) : (
                        <span>{f.value}{f.qty ? ` (${f.qty} pcs)` : ""}</span>
                      )}
                      {f.price ? <span className="text-slate-400 font-mono-nums">+{f.price}</span> : null}
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        )
      )}

      {tab === "forms" && (
        <div className="grid md:grid-cols-[260px_1fr] gap-4">
          <Card className="p-3 border-slate-200/70 space-y-2 h-fit">
            <Button type="button" data-testid="cf-new-form" size="sm" className="w-full bg-orange-500 hover:bg-orange-600 text-slate-900" onClick={() => setEditing(blankForm())}>
              <Plus className="w-4 h-4 mr-1" /> New form
            </Button>
            {items.length === 0 && <div className="text-xs text-slate-500 p-2">No forms yet.</div>}
            {items.map((f) => (
              <button
                key={f.id}
                type="button"
                data-testid={`cf-form-${f.id}`}
                onClick={() => setEditing({ ...blankForm(), ...f, category_ids: f.category_ids || [], product_ids: f.product_ids || [] })}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm ${editing?.id === f.id ? "bg-slate-900 text-white" : "hover:bg-slate-50 text-slate-800"}`}
              >
                <div className="font-medium truncate">{f.name}</div>
                <div className={`text-[11px] ${editing?.id === f.id ? "text-white/70" : "text-slate-500"}`}>
                  {f.scope === "all" ? "All products" : f.scope === "categories" ? `${(f.category_ids || []).length} categories` : `${(f.product_ids || []).length} products`}
                  {f.enabled ? "" : " · off"}
                </div>
              </button>
            ))}
          </Card>

          {!editing ? (
            <Card className="p-8 text-center text-slate-500">Select a form or create a new one.</Card>
          ) : (
            <Card className="p-5 border-slate-200/70 space-y-4" data-testid="cf-builder">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <Label>Form name</Label>
                  <Input className="mt-1" data-testid="cf-form-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                </div>
                <div className="pt-5">
                  <Switch checked={Boolean(editing.enabled)} onCheckedChange={(v) => setEditing({ ...editing, enabled: v })} data-testid="cf-form-enabled" />
                </div>
              </div>
              <div>
                <Label>Button text</Label>
                <Input className="mt-1" data-testid="cf-form-cta" value={editing.cta || ""} onChange={(e) => setEditing({ ...editing, cta: e.target.value })} />
              </div>
              <div>
                <Label>Show this form on</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[
                    ["all", "All products"],
                    ["categories", "Categories"],
                    ["products", "Chosen products"],
                  ].map(([val, label]) => (
                    <button
                      key={val}
                      type="button"
                      data-testid={`cf-scope-${val}`}
                      disabled={val === "all" && taken.allId && taken.allId !== editing.id}
                      onClick={() => setEditing({ ...editing, scope: val })}
                      className={`px-3 py-1.5 rounded-full text-sm border ${editing.scope === val ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-700"} disabled:opacity-40`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {taken.allId && taken.allId !== editing.id && <p className="text-xs text-amber-700 mt-2">Another form is already linked to all products, so this one cannot use “All products”.</p>}
              </div>

              {editing.scope === "categories" && (
                <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 p-2 space-y-1">
                  {categories.length === 0 && <div className="text-xs text-slate-500 p-2">No categories yet.</div>}
                  {categories.map((c) => {
                    const takenBy = taken.categories[c.id];
                    const checked = (editing.category_ids || []).includes(c.id);
                    return (
                      <label key={c.id} className={`flex items-center gap-2 text-sm px-2 py-1 rounded ${takenBy && !checked ? "opacity-50" : "hover:bg-slate-50"}`}>
                        <input type="checkbox" disabled={Boolean(takenBy) && !checked} checked={checked} onChange={() => toggleId("category_ids", c.id, takenBy && !checked)} />
                        <span>{c.name}</span>
                        {takenBy && !checked ? <span className="text-[11px] text-slate-400">Linked to {takenBy.name}</span> : null}
                      </label>
                    );
                  })}
                </div>
              )}

              {editing.scope === "products" && (
                <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 p-2 space-y-1" data-testid="cf-product-list">
                  {products.length === 0 && <div className="text-xs text-slate-500 p-2">No products yet.</div>}
                  {products.map((p) => {
                    const takenBy = taken.products[p.id];
                    const checked = (editing.product_ids || []).includes(p.id);
                    return (
                      <label key={p.id} className={`flex items-center gap-2 text-sm px-2 py-1 rounded ${takenBy && !checked ? "opacity-50" : "hover:bg-slate-50"}`}>
                        <input type="checkbox" disabled={Boolean(takenBy) && !checked} checked={checked} onChange={() => toggleId("product_ids", p.id, takenBy && !checked)} />
                        <span className="truncate">{p.name}</span>
                        {takenBy && !checked ? <span className="text-[11px] text-slate-400 shrink-0">Linked to {takenBy.name}</span> : null}
                      </label>
                    );
                  })}
                </div>
              )}

              <div className="space-y-3">
                <Label>Fields</Label>
                {(editing.fields || []).map((field, fi) => (
                  <div key={field.id} className="rounded-lg border border-slate-200 p-3 space-y-2">
                    <div className="flex gap-2">
                      <Input value={field.label} onChange={(e) => {
                        const fields = [...editing.fields];
                        fields[fi] = { ...field, label: e.target.value };
                        setEditing({ ...editing, fields });
                      }} />
                      <span className="text-xs text-slate-500 self-center shrink-0">{field.type.replace("_", " ")}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setEditing({ ...editing, fields: editing.fields.filter((_, i) => i !== fi) })}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                    {(field.type === "paid_name" || field.type === "paid_image") && (
                      <Input type="number" step="0.01" placeholder="Price" value={field.price} onChange={(e) => {
                        const fields = [...editing.fields];
                        fields[fi] = { ...field, price: e.target.value };
                        setEditing({ ...editing, fields });
                      }} />
                    )}
                    {(field.type === "paid_dropdown" || field.type === "quantity") && (
                      <div className="space-y-1">
                        {(field.options || []).map((opt, oi) => (
                          <div key={opt.id} className="flex gap-2">
                            <Input value={opt.label} onChange={(e) => {
                              const fields = [...editing.fields];
                              const options = [...field.options];
                              options[oi] = { ...opt, label: e.target.value };
                              fields[fi] = { ...field, options };
                              setEditing({ ...editing, fields });
                            }} />
                            {field.type === "quantity" && (
                              <Input type="number" className="w-24" value={opt.qty} onChange={(e) => {
                                const fields = [...editing.fields];
                                const options = [...field.options];
                                options[oi] = { ...opt, qty: e.target.value };
                                fields[fi] = { ...field, options };
                                setEditing({ ...editing, fields });
                              }} />
                            )}
                            <Input type="number" step="0.01" className="w-28" placeholder="Price" value={opt.price} onChange={(e) => {
                              const fields = [...editing.fields];
                              const options = [...field.options];
                              options[oi] = { ...opt, price: e.target.value };
                              fields[fi] = { ...field, options };
                              setEditing({ ...editing, fields });
                            }} />
                            <Button type="button" variant="ghost" size="sm" onClick={() => {
                              const fields = [...editing.fields];
                              fields[fi] = { ...field, options: field.options.filter((_, i) => i !== oi) };
                              setEditing({ ...editing, fields });
                            }}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        ))}
                        <Button type="button" size="sm" variant="outline" onClick={() => {
                          const fields = [...editing.fields];
                          fields[fi] = { ...field, options: [...(field.options || []), { id: `op_${uid().slice(0, 8)}`, label: "Option", price: 0, qty: field.type === "quantity" ? 1 : undefined }] };
                          setEditing({ ...editing, fields });
                        }}>Add option</Button>
                      </div>
                    )}
                  </div>
                ))}
                <div className="flex flex-wrap gap-2">
                  {["paid_name", "paid_dropdown", "paid_image", "quantity"].map((t) => (
                    <Button key={t} type="button" size="sm" variant="outline" data-testid={`cf-add-${t}`} onClick={() => setEditing({ ...editing, fields: [...(editing.fields || []), newCfField(t)] })}>
                      + {t.replace("paid_", "").replace("_", " ")}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <Button type="button" data-testid="cf-save-form" disabled={saving} onClick={save} className="bg-orange-500 hover:bg-orange-600 text-slate-900">{saving ? "Saving…" : "Save form"}</Button>
                {editing.id && <Button type="button" variant="outline" onClick={() => remove(editing.id)}>Delete</Button>}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
